import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const MentionInput = ({
  value, onChange, placeholder = '', className = '',
  minRows = 1, maxRows = 6, disabled = false, onKeyDown, 'data-testid': testId
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeout = useRef(null);

  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 1) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/social/search-users?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setSuggestions(data.users || []);
      setSelectedIdx(0);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const text = e.target.value;
    onChange(text);

    // Detect @ trigger
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = text.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      const query = atMatch[1];
      setMentionStart(cursorPos - query.length - 1);
      setMentionQuery(query);
      setShowSuggestions(true);
      clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => searchUsers(query), 200);
    } else {
      setShowSuggestions(false);
      setMentionQuery('');
    }
  };

  const insertMention = (user) => {
    const before = value.substring(0, mentionStart);
    const after = value.substring(mentionStart + mentionQuery.length + 1);
    const mention = `@[${user.display_name}](user:${user.user_id}) `;
    const newValue = before + mention + after;
    onChange(newValue);
    setShowSuggestions(false);
    setSuggestions([]);

    // Refocus textarea at end of mention
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + mention.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 50);
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx(prev => Math.min(prev + 1, suggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx(prev => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[selectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  useEffect(() => {
    return () => clearTimeout(searchTimeout.current);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const lineH = 24;
      const minH = lineH * minRows;
      const maxH = lineH * maxRows;
      textareaRef.current.style.height = `${Math.min(Math.max(textareaRef.current.scrollHeight, minH), maxH)}px`;
    }
  }, [value, minRows, maxRows]);

  return (
    <div className="relative w-full flex-1 min-w-0">
      {/* Brief 27 oct A.1 (RCA) — la className reçue s'applique au textarea ; le
          wrapper doit lui-même prendre toute la largeur disponible en contexte flex. */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full resize-none outline-none ${className}`}
        data-testid={testId}
        rows={minRows}
      />

      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div
          ref={suggestionsRef}
          className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-[200px] overflow-y-auto"
          data-testid="mention-suggestions"
        >
          {loading && suggestions.length === 0 ? (
            <div className="p-3 text-center text-gray-400 text-sm">Recherche...</div>
          ) : (
            suggestions.map((user, idx) => (
              <button
                key={user.user_id}
                onClick={() => insertMention(user)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  idx === selectedIdx ? 'bg-purple-50' : 'hover:bg-gray-50'
                }`}
                data-testid={`mention-user-${user.user_id}`}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={resolveUrl(user.avatar)} />
                  <AvatarFallback className="bg-purple-100 text-purple-600 text-xs">{user.display_name?.[0]}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-gray-900">{user.display_name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Render mention-rich text with clickable @mentions
const URL_IN_TEXT = /(https?:\/\/[^\s<>"')\]]+)/g;

// Brief 27 oct LOT A.3 / H — les URL brutes dans un texte deviennent cliquables
// (même origine → navigation interne) et les mots longs sont coupés proprement
// pour ne jamais déborder sur mobile.
const linkify = (content, navigate, keyPrefix) => {
  const out = [];
  let last = 0;
  let m;
  URL_IN_TEXT.lastIndex = 0;
  while ((m = URL_IN_TEXT.exec(content)) !== null) {
    if (m.index > last) out.push(content.substring(last, m.index));
    const href = m[1];
    let internal = null;
    try {
      const u = new URL(href);
      if (typeof window !== 'undefined' && u.origin === window.location.origin) internal = u.pathname + u.search + u.hash;
    } catch (e) { /* ignore */ }
    out.push(
      <a
        key={`${keyPrefix}-${m.index}`}
        href={href}
        target={internal ? undefined : '_blank'}
        rel={internal ? undefined : 'noopener noreferrer'}
        onClick={(e) => {
          e.stopPropagation();
          if (internal && navigate) { e.preventDefault(); navigate(internal); }
        }}
        className="text-purple-600 dark:text-purple-400 underline underline-offset-2 hover:text-purple-700 break-all"
        data-testid="text-link"
      >
        {href.replace(/^https?:\/\//, '').replace(/\/$/, '')}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < content.length) out.push(content.substring(last));
  return out;
};

export const RenderMentionText = ({ text, navigate, className = '' }) => {
  if (!text) return null;
  const wrapCls = `${className} break-words [overflow-wrap:anywhere]`.trim();
  const mentionRegex = /@\[([^\]]+)\]\(user:([a-z0-9_]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
    }
    parts.push({ type: 'mention', name: match[1], userId: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }

  if (parts.length === 0) return <span className={wrapCls}>{linkify(text, navigate, 't')}</span>;

  return (
    <span className={wrapCls}>
      {parts.map((part, i) =>
        part.type === 'mention' ? (
          <span
            key={i}
            onClick={() => navigate && navigate(`/app/profile/${part.userId}`)}
            className="text-purple-600 font-semibold cursor-pointer hover:underline"
            data-testid={`mention-link-${part.userId}`}
          >
            @{part.name}
          </span>
        ) : (
          <span key={i}>{linkify(part.content, navigate, i)}</span>
        )
      )}
    </span>
  );
};

export default MentionInput;
